import { faChevronCircleLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, ReactNode } from 'react';
import {
  MetaLabelProvider,
  MetaLabels,
} from 'src/components/data-display/meta-label';
import { StatusDisplayProvider } from 'src/components/data-display/status-display';
import { Divider } from 'src/components/dividers';
import { Heading } from 'src/components/text/heading';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import type { Referrer } from 'src/hooks/use-referrer';

type BackLinkProps = {
  referrer: Referrer;
};

const BackToLink: FC<BackLinkProps> = ({ referrer }) => {
  return (
    <span>
      {referrer.createReferrerLink((referrerState) => ({
        'data-testid': 'referrer-back-link',
        children: (
          <>
            <FontAwesomeIcon icon={faChevronCircleLeft} aria-hidden /> Back to{' '}
            {referrerState.label}
          </>
        ),
      }))}
    </span>
  );
};

/**
 * @deprecated Use `<PageHeader referrer={...} />` instead.
 * The component was intended to be used when the page does not render `<PageHeader />` yet, but it is now recommended to use `<PageHeader />` directly.
 */
export const PageHeaderBackLink = BackToLink;

type PageHeaderProps = {
  title: ReactNode | string;
  description?: ReactNode | string;
  /** Intended for one or more `<MetaLabel />` components. When passing multiple, wrap them in a `Fragment` */
  metaLabels?: ReactNode;
  /**  Displays statistics or summary metrics related to the current page or content. When `metaLabels` are provided, they are displayed alongside them. */
  stats?: ReactNode | string;
  /** Intended for `<StatusDisplay />` component. */
  status?: ReactNode;
  /** The `referrer` object resulting from `useReferrer()` set in the parent component (usually a Page) is passed to this prop in order to show the back link. */
  referrer?: Referrer;
  /** Intended to display no more than 2 actions. If you want to insert more actions/buttons, consider using `<Split Actions /> component to group them. */
  actions?: ReactNode;
  /** The `backLink` object resulting from `useReferrer()` set in the parent component (usually a Page) is passed to this prop in order to show the back link. */
  backLink?: ReactNode;
  'data-testid'?: string;
};
export const PageHeader: FC<PageHeaderProps> = (props) => {
  const {
    title,
    metaLabels,
    stats,
    description,
    status,
    actions,
    referrer,
    ['data-testid']: testId = 'page-header',
  } = props;
  const { minDesktop } = useBreakpoints();
  return (
    <div className="grid items-center gap-sm" data-testid={testId}>
      {referrer && <BackToLink referrer={referrer} />}
      <div
        className={`
          grid auto-cols-[fit-content()] grid-flow-row items-center
          justify-between gap-md
          lg:grid-flow-col lg:gap-xl
        `}
      >
        <div className="grid items-center gap-xs">
          <Heading mode="headline" size="lg" data-testid={`${testId}-title`}>
            {title}
          </Heading>
          {(!!metaLabels || !!stats || !!description) && (
            <div className="grid items-center justify-items-start gap-md">
              {(!!metaLabels || !!stats) && (
                <div className="grid grid-flow-col items-center gap-lg">
                  {!!metaLabels && (
                    <MetaLabelProvider value="page">
                      <MetaLabels>{metaLabels}</MetaLabels>
                    </MetaLabelProvider>
                  )}
                  {!!metaLabels && !!stats && <Divider direction="vertical" />}
                  {!!stats && (
                    <div className="text-foreground-surface-subdued">
                      {stats}
                    </div>
                  )}
                </div>
              )}
              {!!description && (
                <div
                  className="text-foreground-surface-subdued"
                  data-testid={`${testId}-description`}
                >
                  {description}
                </div>
              )}
            </div>
          )}
        </div>
        {(!!status || !!actions) && (
          <div
            className={`
              grid grid-flow-row items-center justify-start gap-md
              lg:grid-flow-col lg:gap-xl
            `}
          >
            {!!status && (
              <StatusDisplayProvider value="page">
                <div
                  className="grid items-center"
                  data-testid={`${testId}-status`}
                >
                  {status}
                </div>
              </StatusDisplayProvider>
            )}
            {minDesktop && !!status && !!actions && (
              <Divider direction="vertical" />
            )}
            {!!actions && (
              <div
                className="grid grid-cols-[auto_auto] items-center gap-md"
                data-testid={`${testId}-actions`}
              >
                {actions}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
